// ============================================
// DATES ET HEURES : TOUJOURS À L'HEURE DE PARIS
// ============================================
// Signalement de Nathan du 19/10/2026 : « le fuseau horaire n'est pas bon ».
// Le SAV travaille à l'heure de Paris : toute date affichée ou filtrée l'est
// en Europe/Paris, quel que soit le fuseau du navigateur (poste réglé en UTC,
// VPN, déplacement), et l'heure d'été est gérée par Intl.
//   - Ne jamais appeler toLocaleString / toLocaleDateString /
//     toLocaleTimeString ni Intl.DateTimeFormat sans timeZone : passer par
//     ce module.
//   - Un « jour » (AAAA-MM-JJ) est un jour du calendrier de Paris : jamais
//     toISOString().slice(0, 10), qui donne le jour UTC (la veille entre
//     minuit et 2 h, la veille du 1er du mois pour new Date(a, m, 1)).
//   - Le serveur lit dateFrom / dateTo / updatedFrom de la même façon : un
//     jour seul y est pris à minuit, heure de Paris (validate.middleware).
// Helpers purs, sans React. Le même module existe dans l'espace client.

export const FUSEAU = 'Europe/Paris';
export const LOCALE = 'fr-FR';

export type EntreeDate = string | number | Date;

/** Instant valide, ou null (valeur absente, vide ou illisible). */
export function versDate(v: EntreeDate | null | undefined): Date | null {
  if (v === null || v === undefined || v === '') return null;
  const d = v instanceof Date ? new Date(v.getTime()) : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

const formateurs = new Map<string, Intl.DateTimeFormat>();

/** Formateur fr-FR à l'heure de Paris (mis en cache : Intl est coûteux à créer). */
function formateur(options: Intl.DateTimeFormatOptions, locale = LOCALE): Intl.DateTimeFormat {
  const cle = locale + JSON.stringify(options);
  let f = formateurs.get(cle);
  if (!f) {
    f = new Intl.DateTimeFormat(locale, { ...options, timeZone: FUSEAU });
    formateurs.set(cle, f);
  }
  return f;
}

/** Formatage libre à l'heure de Paris ; `vide` si la date manque ou est illisible. */
export function formatParis(v: EntreeDate | null | undefined, options: Intl.DateTimeFormatOptions, vide = ''): string {
  const d = versDate(v);
  return d ? formateur(options).format(d) : vide;
}

/** « 19/10/2026 ». */
export const formatDate = (v: EntreeDate | null | undefined, vide = ''): string =>
  formatParis(v, { day: '2-digit', month: '2-digit', year: 'numeric' }, vide);

/** « 19 octobre 2026 ». */
export const formatDateLongue = (v: EntreeDate | null | undefined, vide = ''): string =>
  formatParis(v, { day: 'numeric', month: 'long', year: 'numeric' }, vide);

/** « 19/10 ». */
export const formatDateCourte = (v: EntreeDate | null | undefined, vide = ''): string =>
  formatParis(v, { day: '2-digit', month: '2-digit' }, vide);

/** « 19/10/2026 14:05:33 » (même rendu que l'ancien toLocaleString('fr-FR')). */
export const formatDateTime = (v: EntreeDate | null | undefined, vide = ''): string =>
  formatParis(v, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }, vide);

/** « 19/10/2026 14:05 ». */
export const formatDateHeure = (v: EntreeDate | null | undefined, vide = ''): string =>
  formatParis(v, { dateStyle: 'short', timeStyle: 'short' }, vide);

/** « 19 octobre 2026 à 14:05 ». */
export const formatDateHeureLongue = (v: EntreeDate | null | undefined, vide = ''): string =>
  formatParis(v, { dateStyle: 'long', timeStyle: 'short' }, vide);

/** « 14:05 ». */
export const formatTime = (v: EntreeDate | null | undefined, vide = ''): string =>
  formatParis(v, { hour: '2-digit', minute: '2-digit' }, vide);

// ---------------------------------------------------------------------------
// Jours du calendrier de Paris (AAAA-MM-JJ)
// ---------------------------------------------------------------------------

export interface PartiesParis {
  annee: number;
  mois: number;
  jour: number;
  heure: number;
  minute: number;
  seconde: number;
}

/** Année, mois (1-12), jour, heure, minute et seconde de l'instant, à Paris. */
export function partiesParis(v: EntreeDate = new Date()): PartiesParis {
  const d = versDate(v);
  if (!d) throw new RangeError('Date invalide');
  const p: Record<string, number> = {};
  for (const x of formateur({
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
  }, 'en-GB').formatToParts(d)) {
    if (x.type !== 'literal') p[x.type] = Number(x.value);
  }
  return { annee: p.year, mois: p.month, jour: p.day, heure: p.hour % 24, minute: p.minute, seconde: p.second };
}

const deux = (n: number) => String(n).padStart(2, '0');

/** Jour de Paris de l'instant, « AAAA-MM-JJ » (aujourd'hui par défaut) ; '' si la date est illisible. */
export function jourParis(v: EntreeDate = new Date()): string {
  if (!versDate(v)) return '';
  const p = partiesParis(v);
  return `${p.annee}-${deux(p.mois)}-${deux(p.jour)}`;
}

/** Année à Paris (année en cours par défaut) ; NaN si la date est illisible. */
export const anneeParis = (v: EntreeDate = new Date()): number => (versDate(v) ? partiesParis(v).annee : NaN);

/** Jour décalé de n jours (calendrier pur, sans fuseau ni heure d'été). */
export function decalerJour(jour: string, n: number): string {
  const [a, m, j] = jour.split('-').map(Number);
  return new Date(Date.UTC(a, m - 1, j + n)).toISOString().slice(0, 10);
}

/** Vrai si les deux instants tombent le même jour à Paris. */
export function memeJourParis(a: EntreeDate, b: EntreeDate): boolean {
  return jourParis(a) === jourParis(b);
}

/** Vrai si l'instant est aujourd'hui, à Paris. */
export const estAujourdhuiParis = (v: EntreeDate, maintenant: EntreeDate = new Date()): boolean =>
  jourParis(v) === jourParis(maintenant);

/** Vrai si l'instant est hier, à Paris. */
export const estHierParis = (v: EntreeDate, maintenant: EntreeDate = new Date()): boolean =>
  jourParis(v) === decalerJour(jourParis(maintenant), -1);

/** Premier jour du mois en cours à Paris, « AAAA-MM-01 ». */
export function debutDuMoisParis(maintenant: EntreeDate = new Date()): string {
  return `${jourParis(maintenant).slice(0, 7)}-01`;
}

/** Dernier jour du mois précédent à Paris. */
export function finDuMoisPrecedentParis(maintenant: EntreeDate = new Date()): string {
  return decalerJour(debutDuMoisParis(maintenant), -1);
}

/** Premier jour du mois précédent à Paris. */
export function debutDuMoisPrecedentParis(maintenant: EntreeDate = new Date()): string {
  return `${finDuMoisPrecedentParis(maintenant).slice(0, 7)}-01`;
}

/** 1er janvier de l'année en cours à Paris. */
export function debutDeLAnneeParis(maintenant: EntreeDate = new Date()): string {
  return `${jourParis(maintenant).slice(0, 4)}-01-01`;
}

/**
 * Un instant situé dans le jour donné (midi UTC, soit 13 h ou 14 h à Paris) :
 * pour formater un jour AAAA-MM-JJ avec Intl sans risque de changer de date.
 */
export function instantDuJour(jour: string): Date {
  return new Date(`${jour}T12:00:00Z`);
}

/** Formate un jour AAAA-MM-JJ (par ex. { weekday: 'short' } → « lun. »). */
export const formatJour = (jour: string, options: Intl.DateTimeFormatOptions): string =>
  formatParis(instantDuJour(jour), options);

// ---------------------------------------------------------------------------
// Périodes prédéfinies des filtres (jours de Paris, bornes incluses)
// ---------------------------------------------------------------------------

export interface PeriodePredefinie {
  label: string;
  /** [du, au] en jours de Paris, AAAA-MM-JJ, bornes incluses. */
  calculer: (maintenant?: EntreeDate) => [string, string];
}

export const PERIODES_PREDEFINIES: PeriodePredefinie[] = [
  { label: "Aujourd'hui", calculer: (n = new Date()) => { const j = jourParis(n); return [j, j]; } },
  // Sept jours, aujourd'hui compris (comme la consommation IA).
  { label: '7 derniers jours', calculer: (n = new Date()) => { const j = jourParis(n); return [decalerJour(j, -6), j]; } },
  { label: 'Ce mois', calculer: (n = new Date()) => [debutDuMoisParis(n), jourParis(n)] },
  { label: 'Mois dernier', calculer: (n = new Date()) => [debutDuMoisPrecedentParis(n), finDuMoisPrecedentParis(n)] },
  { label: 'Cette année', calculer: (n = new Date()) => [debutDeLAnneeParis(n), jourParis(n)] },
];
